import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { hasProAccess } from "@/lib/plans";
import { fmtCurrency, fmtCompact, fmtDate } from "@/lib/format";
import { ACCOUNT_TYPES, accountTypeMeta, CHART_PALETTE, RANGES } from "@/lib/investmentTypes";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import UpsellCard from "@/components/UpsellCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    CandlestickChart, Plus, Pencil, Trash2, TrendingUp, TrendingDown, ArrowRight, PlusCircle,
    AreaChart as AreaChartIcon, LineChart as LineChartIcon, BarChart3, PieChart as PieChartIcon,
    LayoutGrid, List as ListIcon, Users, User as UserIcon,
} from "lucide-react";
import {
    ComposedChart, Area, Line, Bar, PieChart, Pie, Cell,
    ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

// ISO "YYYY-MM-DD" -> compact tick label, parsed as local date (avoids UTC shift).
function tickDate(v) {
    const [y, m, d] = v.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const CHART_TYPES = [
    { value: "area", label: "Area", icon: AreaChartIcon },
    { value: "line", label: "Line", icon: LineChartIcon },
    { value: "bar", label: "Bar", icon: BarChart3 },
    { value: "pie", label: "Allocation", icon: PieChartIcon },
];

export default function Investments() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";
    const navigate = useNavigate();
    const { isMember, memberName } = useHousehold();
    const canView = hasProAccess(user, isMember);

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState("6m");
    const [chartType, setChartType] = useState("area");
    const [view, setView] = useState("list");
    // Opens with the user's preferred Combined/Just me default (Settings → Preferences).
    const [scope, setScope] = useState(() => user?.default_scope || "mine");
    const [chartData, setChartData] = useState({ points: [], accounts: [] });
    const [chartLoading, setChartLoading] = useState(true);
    const [hiddenIds, setHiddenIds] = useState(new Set());

    const [editing, setEditing] = useState(null);
    const [logging, setLogging] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    const loadAccounts = async (sc) => {
        if (!canView) { setLoading(false); return; }
        setLoading(true);
        try {
            const params = {};
            if (isMember) params.scope = sc ?? scope;
            const { data } = await api.get("/investments", { params });
            setAccounts(data);
        } finally { setLoading(false); }
    };

    const loadChart = async (r, sc) => {
        if (!canView) { setChartLoading(false); return; }
        setChartLoading(true);
        try {
            const params = { range: r };
            if (isMember) params.scope = sc ?? scope;
            const { data } = await api.get("/investments/analytics", { params });
            setChartData(data);
        } finally { setChartLoading(false); }
    };

    useEffect(() => { loadAccounts(); loadChart(range); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope, isMember, canView]);
    useEffect(() => { loadChart(range); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range]);

    const reloadAll = () => { loadAccounts(); loadChart(range); };

    const onDelete = async () => {
        if (!deleteId) return;
        try {
            await api.delete(`/investments/${deleteId}`);
            toast.success("Account removed");
            reloadAll();
        } catch { toast.error("Failed to delete"); }
        finally { setDeleteId(null); }
    };

    const toggleAccount = (id) => {
        setHiddenIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const totalValue = accounts.reduce((s, a) => s + parseFloat(a.current_value), 0);
    const totalChange = accounts.reduce((s, a) => s + parseFloat(a.change_amount), 0);
    const totalInitial = totalValue - totalChange;
    const totalChangePct = totalInitial !== 0 ? (totalChange / totalInitial) * 100 : 0;
    const isUp = totalChange >= 0;

    const allocationData = accounts
        .map((a, i) => ({
            id: a.id, name: a.name, value: parseFloat(a.current_value) || 0,
            color: a.color || accountTypeMeta(a.account_type).color || CHART_PALETTE[i % CHART_PALETTE.length],
        }))
        .filter(d => d.value > 0)
        .sort((a, b) => b.value - a.value);
    const allocationTotal = allocationData.reduce((s, d) => s + d.value, 0);

    if (!canView) {
        return (
            <div className="space-y-4" data-testid="investments-page">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Investments</h1>
                    <p className="text-sm text-slate-500 mt-1">Track balances across brokerage, retirement, and crypto accounts.</p>
                </div>
                <UpsellCard
                    feature="Investments"
                    description="Upgrade to Pro to track balances across brokerage, retirement, and crypto accounts."
                />
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="investments-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Investments</h1>
                    <p className="text-sm text-slate-500 mt-1">Track balances across brokerage, retirement, and crypto accounts.</p>
                </div>
                <div className="flex items-center gap-2">
                    {isMember ? (
                        <Tabs value={scope} onValueChange={setScope}>
                            <TabsList className="bg-slate-100 rounded-xl p-1">
                                <TabsTrigger data-testid="inv-scope-household" value="household" className="rounded-lg data-[state=active]:bg-white gap-1.5">
                                    <Users className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Combined</span>
                                </TabsTrigger>
                                <TabsTrigger data-testid="inv-scope-mine" value="mine" className="rounded-lg data-[state=active]:bg-white gap-1.5">
                                    <UserIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Just me</span>
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    ) : null}
                    <Button onClick={() => setEditing({ _new: true, account_type: "brokerage" })} data-testid="new-account-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl gap-1.5">
                        <Plus className="w-4 h-4" /> New account
                    </Button>
                </div>
            </div>

            {accounts.length > 0 && (
                <div className="relative card-elev rounded-2xl p-6 sm:p-7" data-testid="portfolio-chart">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
                        <div className="pr-24 sm:pr-0">
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-1">Total portfolio value</p>
                            <div className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tabular-nums" data-testid="portfolio-total">{fmtCurrency(totalValue, cur)}</div>
                            <div className={`inline-flex items-center gap-1 mt-1.5 text-sm font-semibold ${isUp ? "text-emerald-600" : "text-rose-600"}`}>
                                {isUp ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                {isUp ? "+" : ""}{fmtCurrency(totalChange, cur)} ({isUp ? "+" : ""}{totalChangePct.toFixed(1)}%) all time
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            {/* Mobile: dropdown, pinned to the card's top-right corner, in line with the "Total portfolio value" label. Desktop (sm+): unchanged icon-button row, back in normal flow. */}
                            <Select value={chartType} onValueChange={setChartType}>
                                <SelectTrigger data-testid="chart-type-select-mobile" className="sm:hidden absolute top-6 right-6 h-8 rounded-lg w-[104px] px-2 text-xs z-10"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CHART_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <div className="hidden sm:inline-flex bg-slate-100 rounded-xl p-1 gap-0.5" data-testid="chart-type-tabs">
                                {CHART_TYPES.map(ct => {
                                    const Icon = ct.icon;
                                    const active = chartType === ct.value;
                                    return (
                                        <button
                                            key={ct.value} onClick={() => setChartType(ct.value)} data-testid={`chart-type-${ct.value}`}
                                            title={ct.label}
                                            className={`p-2 rounded-lg transition-all ${active ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                                        >
                                            <Icon className="w-4 h-4" />
                                        </button>
                                    );
                                })}
                            </div>
                            {chartType !== "pie" ? (
                                <Tabs value={range} onValueChange={setRange}>
                                    <TabsList data-testid="range-tabs" className="bg-slate-100 rounded-xl p-1">
                                        {RANGES.map(r => (
                                            <TabsTrigger key={r.value} value={r.value} data-testid={`range-${r.value}`} className="rounded-lg data-[state=active]:bg-white px-3">{r.label}</TabsTrigger>
                                        ))}
                                    </TabsList>
                                </Tabs>
                            ) : (
                                <span className="text-xs text-slate-500">Today's allocation</span>
                            )}
                        </div>
                    </div>

                    <div className="h-72">
                        {chartType === "pie" ? (
                            <AllocationPie data={allocationData} total={allocationTotal} cur={cur} />
                        ) : chartLoading ? (
                            <div className="h-full grid place-items-center text-sm text-slate-400">Loading chart…</div>
                        ) : chartData.points.length === 0 ? (
                            <div className="h-full grid place-items-center text-sm text-slate-400 text-center px-6">No balances logged in this range yet — try a wider range or log a balance below.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={chartData.points} margin={{ left: -12, right: 8, top: 8, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="g-total" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.35} />
                                            <stop offset="100%" stopColor="#4F46E5" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="date" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={tickDate} minTickGap={40} />
                                    <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => fmtCompact(v, cur)} />
                                    <Tooltip
                                        labelFormatter={(v) => fmtDate(v)}
                                        formatter={(value, name) => [fmtCurrency(value, cur), name]}
                                        contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,23,42,0.06)" }}
                                    />
                                    {chartType === "area" && (
                                        <Area type="monotone" dataKey="total" name="Total" stroke="#4F46E5" fill="url(#g-total)" strokeWidth={2.5} />
                                    )}
                                    {chartType === "line" && (
                                        <Line type="monotone" dataKey="total" name="Total" stroke="#4F46E5" strokeWidth={3} dot={false} />
                                    )}
                                    {(chartType === "area" || chartType === "line") && chartData.accounts.map((a, i) => hiddenIds.has(a.id) ? null : (
                                        <Line key={a.id} type="monotone" dataKey={a.id} name={a.name} stroke={a.color || CHART_PALETTE[i % CHART_PALETTE.length]} strokeWidth={1.75} dot={false} connectNulls={false} />
                                    ))}
                                    {chartType === "bar" && chartData.accounts.map((a, i) => hiddenIds.has(a.id) ? null : (
                                        <Bar key={a.id} dataKey={a.id} name={a.name} stackId="portfolio" fill={a.color || CHART_PALETTE[i % CHART_PALETTE.length]} radius={i === chartData.accounts.length - 1 ? [4, 4, 0, 0] : 0} />
                                    ))}
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {chartType !== "pie" && chartData.accounts.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                            {chartData.accounts.map((a, i) => {
                                const hidden = hiddenIds.has(a.id);
                                const color = a.color || CHART_PALETTE[i % CHART_PALETTE.length];
                                return (
                                    <button
                                        key={a.id} onClick={() => toggleAccount(a.id)} data-testid={`legend-${a.id}`}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${hidden ? "border-slate-200 text-slate-400 bg-slate-50" : "border-transparent"}`}
                                        style={!hidden ? { backgroundColor: `${color}18`, color } : {}}
                                    >
                                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: hidden ? "#CBD5E1" : color }} />
                                        {a.name}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {chartType === "pie" && allocationData.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-100 space-y-2" data-testid="allocation-list">
                            {allocationData.map(d => {
                                const pct = allocationTotal > 0 ? (d.value / allocationTotal) * 100 : 0;
                                return (
                                    <div key={d.id} className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                                            <span className="text-slate-700 truncate">{d.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className="text-xs text-slate-400 tabular-nums">{pct.toFixed(1)}%</span>
                                            <span className="font-semibold text-slate-900 tabular-nums">{fmtCurrency(d.value, cur)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {loading ? (
                <div className="card-elev rounded-2xl p-10 text-center text-slate-400 text-sm">Loading…</div>
            ) : accounts.length === 0 ? (
                <div className="card-elev rounded-2xl">
                    <EmptyState icon={CandlestickChart} title="No investment accounts yet"
                        description="Add Robinhood, Fidelity, your 401(k), HSA, or any account and log its balance to start tracking your portfolio."
                        action={<Button onClick={() => setEditing({ _new: true, account_type: "brokerage" })} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"><Plus className="w-4 h-4 mr-1.5" /> New account</Button>}
                    />
                </div>
            ) : (
                <>
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold text-slate-900">Accounts</h2>
                    {/* Cards/list toggle only matters at lg+ — mobile always uses the compact stacked list below */}
                    <Tabs value={view} onValueChange={setView} className="hidden lg:block">
                        <TabsList className="bg-slate-100 rounded-xl p-1">
                            <TabsTrigger data-testid="accounts-view-cards" value="cards" className="rounded-lg data-[state=active]:bg-white px-2.5">
                                <LayoutGrid className="w-4 h-4" />
                            </TabsTrigger>
                            <TabsTrigger data-testid="accounts-view-list" value="list" className="rounded-lg data-[state=active]:bg-white px-2.5">
                                <ListIcon className="w-4 h-4" />
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>

                {/* Mobile: compact stacked list, no horizontal scroll — always shown below lg regardless of the cards/list toggle */}
                <div className="lg:hidden card-elev rounded-2xl divide-y divide-slate-100 overflow-hidden" data-testid="accounts-mobile-list">
                    {accounts.map(a => {
                        const meta = accountTypeMeta(a.account_type);
                        const Icon = meta.icon;
                        const value = parseFloat(a.current_value);
                        const change = parseFloat(a.change_amount);
                        const up = change >= 0;
                        return (
                            <div key={a.id} data-testid={`m-account-${a.id}`} onClick={() => navigate(`/investments/${a.id}`)} className="flex items-center gap-2.5 px-3 py-2.5 active:bg-slate-50/60 transition-colors cursor-pointer">
                                <span className="w-9 h-9 rounded-lg grid place-items-center flex-shrink-0" style={{ backgroundColor: `${a.color || meta.color}18`, color: a.color || meta.color }}>
                                    <Icon className="w-4 h-4" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                                            {a.name}
                                            {isMember && scope === "household" && a.user_id !== user.id ? (
                                                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(a.user_id)}</span>
                                            ) : null}
                                        </span>
                                        <span className="font-display font-bold text-xs text-slate-900 tabular-nums flex-shrink-0">{fmtCurrency(value, cur)}</span>
                                    </div>
                                    <div className="flex items-center justify-between mt-0.5 text-[11px]">
                                        <span className="text-slate-500">{meta.label}</span>
                                        {a.entry_count > 0 ? (
                                            <span className={`font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>{up ? "+" : ""}{fmtCurrency(change, cur)} ({up ? "+" : ""}{a.change_percent.toFixed(1)}%)</span>
                                        ) : <span className="text-slate-400">No balance yet</span>}
                                    </div>
                                </div>
                                {a.user_id === user.id ? (
                                    <div className="flex flex-col gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                        <button onClick={() => setLogging(a)} data-testid={`m-account-log-${a.id}`} className="p-1 rounded-md text-indigo-600 hover:bg-indigo-50">
                                            <PlusCircle className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => setEditing(a)} data-testid={`m-account-edit-${a.id}`} className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>

                {/* Desktop (lg+): unchanged cards/list toggle */}
                <div className="hidden lg:block">
                {view === "list" ? (
                <div className="card-elev rounded-2xl overflow-hidden" data-testid="accounts-list">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50/70 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left font-semibold px-5 py-3">Account</th>
                                    <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Type</th>
                                    <th className="text-right font-semibold px-5 py-3">Value</th>
                                    <th className="text-right font-semibold px-5 py-3 hidden sm:table-cell">All-time change</th>
                                    <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">Updated</th>
                                    <th className="text-right font-semibold px-5 py-3 w-40"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {accounts.map(a => {
                                    const meta = accountTypeMeta(a.account_type);
                                    const Icon = meta.icon;
                                    const value = parseFloat(a.current_value);
                                    const change = parseFloat(a.change_amount);
                                    const up = change >= 0;
                                    return (
                                        <tr key={a.id} data-testid={`account-row-${a.id}`} onClick={() => navigate(`/investments/${a.id}`)} className="hover:bg-slate-50/50 transition-colors cursor-pointer">
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-2.5">
                                                    <span className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0" style={{ backgroundColor: `${a.color || meta.color}18`, color: a.color || meta.color }}>
                                                        <Icon className="w-4 h-4" />
                                                    </span>
                                                    <span className="font-semibold text-slate-900 truncate">{a.name}</span>
                                                    {isMember && scope === "household" && a.user_id !== user.id ? (
                                                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(a.user_id)}</span>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 hidden md:table-cell">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700">{meta.label}</span>
                                            </td>
                                            <td className="px-5 py-3.5 text-right font-display font-bold text-slate-900 tabular-nums">{fmtCurrency(value, cur)}</td>
                                            <td className="px-5 py-3.5 text-right hidden sm:table-cell tabular-nums">
                                                {a.entry_count > 0 ? (
                                                    <span className={`font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>{up ? "+" : ""}{fmtCurrency(change, cur)} ({up ? "+" : ""}{a.change_percent.toFixed(1)}%)</span>
                                                ) : <span className="text-slate-400">—</span>}
                                            </td>
                                            <td className="px-5 py-3.5 hidden lg:table-cell text-slate-600">{a.last_updated ? fmtDate(a.last_updated) : <span className="text-slate-400">—</span>}</td>
                                            <td className="px-5 py-3.5 text-right">
                                                <div className="inline-flex gap-1" onClick={e => e.stopPropagation()}>
                                                    {a.user_id === user.id ? (
                                                        <>
                                                            <button onClick={() => setLogging(a)} data-testid={`row-log-${a.id}`} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100">+ Log</button>
                                                            <button onClick={() => setEditing(a)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                                                            <button onClick={() => setDeleteId(a.id)} className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                                                        </>
                                                    ) : (
                                                        <span className="text-xs text-slate-400">{memberName(a.user_id)}'s</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="accounts-grid">
                    {accounts.map(a => {
                        const meta = accountTypeMeta(a.account_type);
                        const Icon = meta.icon;
                        const value = parseFloat(a.current_value);
                        const change = parseFloat(a.change_amount);
                        const up = change >= 0;
                        return (
                            <div key={a.id} data-testid={`account-${a.id}`} className="card-elev rounded-2xl p-5 group">
                                <div className="flex items-start justify-between mb-3">
                                    <Link to={`/investments/${a.id}`} data-testid={`account-open-${a.id}`} className="flex items-center gap-3 min-w-0 -m-1 p-1 rounded-xl hover:bg-slate-50/60 transition-colors flex-1">
                                        <span className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0" style={{ backgroundColor: `${a.color || meta.color}18`, color: a.color || meta.color }}>
                                            <Icon className="w-5 h-5" />
                                        </span>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <h3 className="font-display text-base font-bold text-slate-900 truncate group-hover:text-indigo-700 transition-colors">{a.name}</h3>
                                                <ArrowRight className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                                                {isMember && scope === "household" && a.user_id !== user.id ? (
                                                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(a.user_id)}</span>
                                                ) : null}
                                            </div>
                                            <p className="text-xs text-slate-500">{meta.label}</p>
                                        </div>
                                    </Link>
                                    {a.user_id === user.id ? (
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                            <button onClick={() => setEditing(a)} data-testid={`account-edit-${a.id}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                                            <button onClick={() => setDeleteId(a.id)} data-testid={`account-delete-${a.id}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    ) : null}
                                </div>
                                <div className="font-display text-2xl font-extrabold text-slate-900 tabular-nums mb-1">{fmtCurrency(value, cur)}</div>
                                {a.entry_count > 0 ? (
                                    <div className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
                                        {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                                        {up ? "+" : ""}{fmtCurrency(change, cur)} ({up ? "+" : ""}{a.change_percent.toFixed(1)}%) all time
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-400">No balance logged yet</p>
                                )}
                                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                                    <span className="text-xs text-slate-500">{a.last_updated ? `Updated ${fmtDate(a.last_updated)}` : "—"}</span>
                                    {a.user_id === user.id ? (
                                        <button onClick={() => setLogging(a)} data-testid={`log-balance-${a.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                                            <PlusCircle className="w-3.5 h-3.5" /> Log balance
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
                )}
                </div>
                </>
            )}

            <AccountEditor item={editing} onClose={() => setEditing(null)} onSaved={reloadAll} />
            <LogBalanceDialog account={logging} onClose={() => setLogging(null)} onSaved={reloadAll} />

            <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this account?</AlertDialogTitle>
                        <AlertDialogDescription>All logged balance history for this account will be lost.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function AllocationPie({ data, total, cur }) {
    if (data.length === 0) {
        return <div className="h-full grid place-items-center text-sm text-slate-400 text-center px-6">Log a balance to see your allocation.</div>;
    }
    return (
        <div className="relative h-full">
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie data={data} dataKey="value" nameKey="name" innerRadius={64} outerRadius={104} paddingAngle={2.5} stroke="none">
                        {data.map(d => <Cell key={d.id} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [fmtCurrency(v, cur), n]} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,23,42,0.06)" }} />
                </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs text-slate-500">Total</span>
                <span className="font-display text-xl font-extrabold text-slate-900">{fmtCompact(total, cur)}</span>
            </div>
        </div>
    );
}

function AccountEditor({ item, onClose, onSaved }) {
    const [form, setForm] = useState(item || {});
    const [saving, setSaving] = useState(false);
    useEffect(() => { setForm(item || {}); }, [item]);
    if (!item) return null;

    const submit = async () => {
        if (!form.name) { toast.error("Name is required"); return; }
        try {
            setSaving(true);
            if (form._new) {
                const payload = { name: form.name, account_type: form.account_type || "brokerage", notes: form.notes || null };
                if (form.initial_amount) {
                    payload.initial_amount = parseFloat(form.initial_amount);
                    payload.initial_date = form.initial_date || null;
                }
                await api.post("/investments", payload);
            } else {
                await api.patch(`/investments/${form.id}`, {
                    name: form.name, account_type: form.account_type, notes: form.notes || null,
                });
            }
            toast.success(form._new ? "Account created" : "Updated");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="account-editor">
                <DialogHeader><DialogTitle className="font-display text-xl">{form._new ? "New investment account" : "Edit account"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <FieldL label="Account name"><Input data-testid="ae-name" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Robinhood, Fidelity 401(k)" /></FieldL>
                    <FieldL label="Type">
                        <Select value={form.account_type || "brokerage"} onValueChange={v => setForm({ ...form, account_type: v })}>
                            <SelectTrigger data-testid="ae-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {ACCOUNT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </FieldL>
                    {form._new ? (
                        <div className="grid grid-cols-2 gap-3">
                            <FieldL label="Starting balance (optional)"><Input data-testid="ae-initial" type="number" step="0.01" min="0" value={form.initial_amount || ""} onChange={e => setForm({ ...form, initial_amount: e.target.value })} placeholder="0.00" /></FieldL>
                            <FieldL label="As of date"><Input data-testid="ae-initial-date" type="date" value={form.initial_date || new Date().toISOString().slice(0, 10)} onChange={e => setForm({ ...form, initial_date: e.target.value })} /></FieldL>
                        </div>
                    ) : null}
                    <FieldL label="Notes"><Textarea data-testid="ae-notes" rows={2} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></FieldL>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button data-testid="ae-save" disabled={saving} onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 text-white">{form._new ? "Create" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function LogBalanceDialog({ account, onClose, onSaved }) {
    const { user } = useAuth();
    const cur = user?.currency || "USD";
    const [amount, setAmount] = useState("");
    const [occurredAt, setOccurredAt] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (account) {
            setAmount(""); setNotes("");
            setOccurredAt(new Date().toISOString().slice(0, 10));
        }
    }, [account]);
    if (!account) return null;

    const currentValue = parseFloat(account.current_value) || 0;

    const submit = async () => {
        const amt = parseFloat(amount);
        if (Number.isNaN(amt) || amt < 0) { toast.error("Enter a valid balance"); return; }
        try {
            setSaving(true);
            await api.post(`/investments/${account.id}/entries`, { amount: amt, occurred_at: occurredAt, notes: notes || null });
            toast.success("Balance logged");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!account} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-md rounded-2xl" data-testid="log-balance-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">Log balance — {account.name}</DialogTitle>
                    <p className="text-xs text-slate-500">Current recorded value: <span className="font-semibold text-slate-700 tabular-nums">{fmtCurrency(currentValue, cur)}</span></p>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <FieldL label="Balance"><Input data-testid="lb-amount" type="number" step="0.01" min="0" autoFocus value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" /></FieldL>
                        <FieldL label="As of date"><Input data-testid="lb-date" type="date" value={occurredAt} onChange={e => setOccurredAt(e.target.value)} /></FieldL>
                    </div>
                    <FieldL label="Note (optional)"><Input data-testid="lb-note" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Market update, contribution, etc." /></FieldL>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button data-testid="lb-save" disabled={saving} onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 text-white">Save balance</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldL({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">{label}</Label>
            {children}
        </div>
    );
}
